/**
 * CSK fork of framework CoralLoader with dev preview override support.
 * Keep in sync with packages/duet-framework/src/coral/CoralLoader.tsx when updating.
 */
import { captureException } from "@sentry/nextjs";
import clsx from "clsx";
import type { EventEmitter2 } from "eventemitter2";
import Script from "next/script";
import { FC, useCallback, useEffect, useRef, useState } from "react";

import { useAnalytics } from "@voxmedia/duet-context/context/analytics-context";
import { useAuth } from "@voxmedia/duet-context/context/auth-context";
import { useConfigContext } from "@voxmedia/duet-context/context/config-context";
import { useCoralCommentsContext } from "@voxmedia/duet-context/context/coral-comments-context";
import CoralEvents from "@voxmedia/duet-framework/coral/CoralEvents";
import type { CoralContentInit } from "@voxmedia/duet-framework/coral/types";
import { withGracefulErrorBoundary } from "@voxmedia/duet-ui/errors/GracefulErrorBoundary";
import { waitFor } from "@voxmedia/duet-utils/wait";
import { useCoralPreviewOverride } from "src/context/CoralPreviewOverrideContext";
import { useCskCoralStoryId } from "src/hooks/use-csk-coral-story-id";
import { getCskCoralRootUrl } from "src/utils/coral-dev-proxy";

export interface CoralStreamEmbed {
  get rendered(): boolean;
  render(): void;
  remove(): void;
  login(accessToken: string): void;
}

interface CoralStreamEmbedOptions {
  id: string;
  rootURL: string;
  storyID?: string;
  storyURL?: string;
  autoRender?: boolean;
  events?: (eventEmitter: EventEmitter2) => void;
  dataEvents?: (eventEmitter: EventEmitter2) => void;
  accessToken?: string;
  containerClassName?: string;
  customCSSURL?: string;
  customFontsCSSURL?: string;
  disableDefaultFonts?: boolean;
  customScrollContainer?: HTMLElement | null;
  storyMode?: "COMMENTS" | "QA" | "RATINGS_AND_REVIEWS";
}

interface Coral {
  createStreamEmbed(options: CoralStreamEmbedOptions): CoralStreamEmbed;
}

declare global {
  interface Window {
    Coral?: Coral;
    CoralStreamEmbed?: CoralStreamEmbed;
  }
}

interface Props {
  contentInit: CoralContentInit;
  domID: string;
  customScrollerID?: string;
  onEmbedLoad?: (stream: CoralStreamEmbed) => void;
  containerClassName?: string;
  nodeId?: string;
}

function getCustomCSSURL() {
  if (!process.env.NEXT_PUBLIC_CORAL_CSS) {
    return undefined;
  }
  return !process.env.NEXT_PUBLIC_CORAL_CSS.includes("://")
    ? `${location.origin}${process.env.NEXT_PUBLIC_CORAL_CSS}`
    : process.env.NEXT_PUBLIC_CORAL_CSS;
}

function getCustomRteCssURL() {
  if (!process.env.NEXT_PUBLIC_CORAL_RTE_CSS) {
    return undefined;
  }
  return !process.env.NEXT_PUBLIC_CORAL_RTE_CSS.includes("://")
    ? `${location.origin}${process.env.NEXT_PUBLIC_CORAL_RTE_CSS}`
    : process.env.NEXT_PUBLIC_CORAL_RTE_CSS;
}

const CskCoralLoader: FC<Props> = ({
  contentInit,
  domID,
  customScrollerID,
  onEmbedLoad,
  containerClassName,
}) => {
  const streamRef = useRef<CoralStreamEmbed | null>(null);
  const coralEventsRef = useRef<CoralEvents | null>(null);
  const coralRef = useRef<Coral | null>(null);

  const [streamEmbedCreated, setStreamEmbedCreated] = useState<boolean>(false);

  const auth = useAuth();
  const { coralConfig } = useConfigContext();
  const { updateCommentCount, setLastReadCount } = useCoralCommentsContext();

  if (!coralConfig) {
    throw new Error("missing coral config");
  }

  const analytics = useAnalytics();

  const {
    communityConfig: { ssoAuthDomain, isPrimaryCommunity },
  } = useConfigContext();

  const storyId = useCskCoralStoryId({
    chorusId: contentInit.chorusId,
    chorusUuid: contentInit.chorusUuid,
    globalId: contentInit.globalId,
    nodeId: contentInit.nodeId,
  });
  const previewOverride = useCoralPreviewOverride();
  const commentsEnabled =
    contentInit.comments.enabled || Boolean(previewOverride?.storyId);

  const initCoral = useCallback(
    (coral: Coral) => {
      if (!coralConfig.origin || !commentsEnabled) {
        return;
      }

      coralRef.current = coral;

      const coralRootUrl = getCskCoralRootUrl(coralConfig.origin);

      const storyUrlSource = previewOverride?.storyUrl ?? contentInit.permalink;
      const storyURL = new URL(storyUrlSource);
      // Unlike network CoralLoader, do not rewrite storyURL to localhost in dev.
      // Staging Coral's permitted-domain list includes staging/www.theverge.com
      // URLs, not http://localhost:3005/... (which triggers a permitted-domains error).

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (window as any).coralStoryId = storyId;

      const ssoConfig = ssoAuthDomain
        ? { ssoAuthDomain, isPrimaryCommunity }
        : undefined;

      coralEventsRef.current = analytics
        ? new CoralEvents(analytics, ssoConfig)
        : null;

      streamRef.current = coralRef.current.createStreamEmbed({
        id: domID,
        rootURL: coralRootUrl,
        storyID: storyId,
        storyURL: storyURL.toString(),
        autoRender: false,
        customCSSURL: getCustomCSSURL(),
        customFontsCSSURL: getCustomRteCssURL(),
        containerClassName: clsx(
          containerClassName,
          `rec-threshold-${coralConfig.commentRecThreshold}`,
        ),
        storyMode: contentInit.comments.mode === "qa" ? "QA" : "COMMENTS",
        events: (events) => {
          events.on(
            "count.comments",
            function (data: { storyID: string; number: number }) {
              const coralId = data.storyID;
              const count = data.number;

              if (coralId) {
                updateCommentCount(coralId, count, Date.now());
                void setLastReadCount(coralId, count);
              }
            },
          );
          coralEventsRef.current?.connect(events);
        },
        customScrollContainer: customScrollerID
          ? document.getElementById(customScrollerID)
          : null,
      });

      window.CoralStreamEmbed = streamRef.current;

      if (onEmbedLoad) {
        onEmbedLoad(streamRef.current);
      }

      setStreamEmbedCreated(true);
    },
    [
      analytics,
      commentsEnabled,
      containerClassName,
      coralConfig.commentRecThreshold,
      coralConfig.origin,
      contentInit.comments.mode,
      contentInit.permalink,
      customScrollerID,
      domID,
      isPrimaryCommunity,
      onEmbedLoad,
      previewOverride,
      setLastReadCount,
      ssoAuthDomain,
      storyId,
      updateCommentCount,
    ],
  );

  const onLoad = useCallback(() => {
    if (!window.Coral) {
      waitFor(
        () => window.Coral,
        (coral) => {
          initCoral(coral);
        },
      );
      return;
    }

    initCoral(window.Coral);
  }, [initCoral]);

  useEffect(() => {
    if (window.Coral && !coralRef.current) {
      initCoral(window.Coral);
    }
  }, [initCoral]);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        if (!streamRef.current.rendered) {
          captureException("Trying to remove Coral, while it was not rendered");
        } else {
          streamRef.current.remove();
        }
      }

      coralEventsRef.current?.disconnect();
      coralEventsRef.current = null;
      streamRef.current = null;
      coralRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (
      auth.userLoading ||
      !auth.user?.coral ||
      !streamRef.current ||
      !streamEmbedCreated
    ) {
      return;
    }

    streamRef.current.login(auth.user.coral);
  }, [auth.userLoading, auth.user?.coral, streamEmbedCreated]);

  if (!coralConfig.origin || !commentsEnabled) {
    return null;
  }

  return (
    <>
      <span style={{ display: "none" }}>Comments</span>
      <Script
        src={`${getCskCoralRootUrl(coralConfig.origin)}/assets/js/embed.js?v=${coralConfig.cacheFingerprint}`}
        onLoad={onLoad}
        defer
      />
    </>
  );
};

export default withGracefulErrorBoundary(CskCoralLoader, {
  context: "CskCoralLoaderErrorBoundary",
});
